import React, { useContext, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import * as S from './styles';
import { TitlePage } from '../../../components/newStudent/Title/TitlePage';
import Divider from '../../../components/common/Divider';
import { ticketsServiceNewStudent } from '../../../services/newStudent';
import {
  INewStudentProvider,
  NewStudentProviderContext,
} from '../../../components/newStudent/ProviderNewStudent';
import TicketItem from './components/TicketAlert';
import { AletNotfoundLoading } from '../../../components/newStudent/Alert';
import { emailContract, phoneWhatsapp } from '../../../variables';
import { ButtonSimple } from '../../../components/newStudent/Buttons';
import { IInternalTicketListItem, IInternalTicketShowBody } from '../../../interfaces/newStudent/ticket.interface';
import TicketFilter from './components/TicketFilter';
import CreateTicketModal from './components/CreateTicketModal';

const initialSearchParams: IInternalTicketShowBody = {
  protocol: '',
  initialDate: '',
  finalDate: '',
  status: '',
};

export default function TicketsNewStudent() {
  const { setIsLoading } = useContext(
    NewStudentProviderContext,
  ) as INewStudentProvider;

  const [allTickets, setAllTickets] = useState<IInternalTicketListItem[]>([]);
  const [indexOpen, setIndexOpen] = useState<number | undefined>();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const lastSearchParamsRef = useRef<IInternalTicketShowBody>(initialSearchParams);
  const [searchParams] = useSearchParams();
  const pendingProtocolRef = useRef<string | null>(null);

  const handleSearch = async (
    body: IInternalTicketShowBody,
    options?: { keepOpen?: boolean },
  ) => {
    lastSearchParamsRef.current = body;
    if (!options?.keepOpen) {
      setIndexOpen(undefined);
    }

    const showLoading = !options?.keepOpen;
    if (showLoading) {
      setIsLoading(true);
    }

    try {
      const { result, data } = await ticketsServiceNewStudent.show(body);
      setAllTickets(result ? data : []);
    } catch (error) {
      throw new Error(String(error));
    } finally {
      if (showLoading) {
        setIsLoading(false);
      }
    }
  };

  const refreshList = () =>
    handleSearch(lastSearchParamsRef.current, { keepOpen: true });

  useEffect(() => {
    const protocolFromUrl = searchParams.get('protocolo');
    pendingProtocolRef.current = protocolFromUrl;
    handleSearch(
      protocolFromUrl
        ? { ...initialSearchParams, protocol: protocolFromUrl }
        : initialSearchParams,
    );
  }, [searchParams]);

  useEffect(() => {
    const protocolFromUrl = pendingProtocolRef.current;
    if (!protocolFromUrl || allTickets.length === 0) return;

    const ticketIndex = allTickets.findIndex(
      (ticket) => ticket.protocol === protocolFromUrl,
    );

    if (ticketIndex >= 0) {
      setIndexOpen(ticketIndex);
      pendingProtocolRef.current = null;
    }
  }, [allTickets]);

  return (
    <S.Container>
      <TitlePage
        title="Requerimentos"
        subtitle="Aqui, você tem acesso e acompanha todos os tickets concluídos e em aberto. 
        Basta clicar abaixo em um deles e acompanhar o processo."
        titlePage="Requerimentos"
      />
      <Divider padding />
      <S.ServiceDiv>
        <S.OpenTicket>
          <S.DivInfo>
            <S.KeyValue>
              <S.B>E-mail Atendimento: </S.B>
              <S.P>{emailContract}</S.P>
            </S.KeyValue>
            <S.KeyValue>
              <S.B>Whatsapp: </S.B>
              <S.P>{phoneWhatsapp}</S.P>
            </S.KeyValue>
            <S.KeyValue>
              <S.B>Observação: </S.B>
              <S.P>
                O horário de atendimento é de segunda a quinta, das 08:30 às
                17:30, e na sexta, das 08:30 às 16:30. Não temos atendimento aos
                finais de semana.
              </S.P>
            </S.KeyValue>
          </S.DivInfo>
          <S.DivButtonTicket>
            <ButtonSimple
              color="primary"
              onClick={() => setIsCreateOpen(true)}
              widthSize="large"
            >
              Abrir Novo Ticket
            </ButtonSimple>
          </S.DivButtonTicket>
        </S.OpenTicket>
      </S.ServiceDiv>

      <TicketFilter
        handleSearch={handleSearch}
        initialValues={initialSearchParams}
      />

      <S.DivAlert>
        <AletNotfoundLoading
          toRender={allTickets.length <= 0}
          notFoundText="Nenhum item encontrado."
        />
      </S.DivAlert>
      <S.Content>
        {allTickets.map((item, index) => (
          <TicketItem
            key={item.id}
            item={item}
            index={index}
            indexOpen={indexOpen}
            setIndexOpen={setIndexOpen}
            onRefreshList={refreshList}
          />
        ))}
      </S.Content>

      <CreateTicketModal
        isOpen={isCreateOpen}
        setIsOpen={setIsCreateOpen}
        onRefreshList={refreshList}
      />
    </S.Container>
  );
}
